import { Text, View, StyleSheet } from "@react-pdf/renderer";
import {
  toNumber,
  getIncrementosDeduccionesCalculados,
} from "../../../services/mappings";
import { renderNumberCell } from "../../../services/pdfService";

const styles = StyleSheet.create({
  table: {
    fontSize: 8,
    marginTop: 10,
  },
  row: {
    flexDirection: "row",
  },
  blankHeaderCell: {
    border: "1pt solid #fff",
    paddingVertical: 2,
    paddingHorizontal: 2,
  },
  cell: {
    border: "1pt solid #000",
    paddingVertical: 2,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  groupCell: {
    backgroundColor: "#edad33",
    paddingVertical: 8,
    border: "1pt solid #000",
  },
  hGroup: {
    fontWeight: "bold",
    fontSize: 8,
  },
  rowSpanCell: {
    paddingTop: 9,
    paddingBottom: 9,
    fontWeight: "bold",
  },

  // 100%
  wItem: { width: "4%" },
  wDesc: { width: "21%" },

  // Contrato
  wUnidad: { width: "6%" },
  wScCant: { width: "6%" },
  wScMeses: { width: "5%" },
  wScPU: { width: "7%" },
  wScTot: { width: "7%" },
  wScGroup: { width: "31%" },

  // Reajuste
  wReaFr: { width: "6%" },
  wReaGroup: { width: "6%" },

  // Acumulado mes anterior
  wAmaCant: { width: "6%" },
  wAmaTot: { width: "7%" },
  wAmaGroup: { width: "13%" },

  // Período actual
  wPaCant: { width: "6%" },
  wPaTot: { width: "7%" },
  wPaGroup: { width: "13%" },

  // Total acumulado
  wTaCant: { width: "5%" },
  wTaTot: { width: "7%" },
  wTaGroup: { width: "12%" },

  dataRow: {
    flexDirection: "row",
  },
  dataCell: {
    border: "0.5px solid #000",
    paddingVertical: 2,
    paddingHorizontal: 2,
    justifyContent: "right",
    textAlign: "right",
  },
  descCell: {
    textAlign: "left",
  },
  mainRowText: {
    fontWeight: "bold",
  },
  subRowDescText: {
    paddingLeft: 8,
  },

  numberText: { fontSize: 8 },
  numberTextMillion: { fontSize: 7 },
  numberTextBillion: { fontSize: 6 },
  numberHundredMillion: { fontSize: 6 },
  numberTextStretch: { fontSize: 7, paddingVertical: 1 },
  numberTextMillionStretch: { fontSize: 6 },
  numberTextBillionStretch: { fontSize: 4 },
  numberHundredMillionStretch: { fontSize: 4 },

  incHeaderRow: {
    backgroundColor: "#f2f2f2",
  },
  incSectionLabel: {
    fontWeight: "bold",
  },
  incDetailLabel: {
    fontStyle: "italic",
  },
  subtotalRow: {
    backgroundColor: "#f2f2f2",
  },

  totalRowCell: {
    backgroundColor: "#5bc7c7",
  },
  totalLabel: {
    color: "#fff",
    fontWeight: "bold",
  },
  totalText: {
    color: "#fff",
    fontWeight: "bold",
  },
});

function ItemsTable({
  items,
  incrementos,
  detalles,
  hashRows = [],
  hashTotals = [],
  moneda = "",
}) {
  const {
    incRows,
    dedRows,
    incAma,
    incPa,
    incTa,
    dedAma,
    dedPa,
    dedTa,
    subAma,
    subPa,
    subTa,
  } = getIncrementosDeduccionesCalculados(detalles, incrementos);

  const rows = Array.isArray(items?.data)
    ? items.data
    : Array.isArray(items)
      ? items
      : [];

  const incrementRows = Array.isArray(incrementos?.data)
    ? incrementos.data
    : Array.isArray(incrementos)
      ? incrementos
      : [];

  const getMeses = (item, detalle) =>
    detalle?.montO_TOTCONT_MESES ??
    detalle?.meses ??
    item?.meses ??
    item?.cantidadMeses ??
    "";

  const renderDataRow = (item, isSubPartida = false) => {
    const detalle = item?.detalle;
    const textStyle = isSubPartida ? undefined : styles.mainRowText;

    return (
      <View
        key={`${item.partidA_ID}-${item.subpartidA_ID ?? "main"}-${item.id ?? item.codigo ?? Math.random()}`}
        style={styles.dataRow}
        wrap={false}
      >
        <View style={[styles.dataCell, styles.wItem]}>
          <Text style={[textStyle, isSubPartida ? { fontSize: 6 } : undefined]}>
            {item?.codigo ?? ""}
          </Text>
        </View>

        <View style={[styles.dataCell, styles.wDesc]}>
          <Text
            style={[
              styles.descCell,
              textStyle,
              isSubPartida ? styles.subRowDescText : undefined,
            ]}
          >
            {item?.nombre ?? item?.descripcion ?? ""}
          </Text>
        </View>

        <View style={[styles.dataCell, styles.wUnidad]}>
          <Text style={textStyle}>{item?.unidad ?? ""}</Text>
        </View>

        {renderNumberCell(
          styles,
          detalle?.montO_TOTCONT_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wScCant],
          textStyle,
          false,
          2,
          2,
        )}

        {renderNumberCell(
          styles,
          getMeses(item, detalle),
          [styles.dataCell, styles.wScMeses],
          textStyle,
        )}

        {renderNumberCell(
          styles,
          detalle?.montO_TOTCONT_PRECIO_UNITARIO,
          [styles.dataCell, styles.wScPU],
          textStyle,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        {renderNumberCell(
          styles,
          detalle?.montO_TOTCONT_MONTO_BASE_MC,
          [styles.dataCell, styles.wScTot],
          textStyle,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_FACTOR_REAJUSTE,
          [styles.dataCell, styles.wReaFr],
          textStyle,
          false,
          2,
          4,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_ANTERIOR_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wAmaCant],
          textStyle,
          false,
          2,
          2,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_ANTERIOR_MONTO_PAGO_MC,
          [styles.dataCell, styles.wAmaTot],
          textStyle,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wPaCant],
          textStyle,
          true,
          2,
          2,
        )}

        {renderNumberCell(
          styles,
          detalle?.eP_MES_MONTO_PAGO_MC,
          [styles.dataCell, styles.wPaTot],
          textStyle,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_CANTIDAD_CONCEPTO_PAGO,
          [styles.dataCell, styles.wTaCant],
          textStyle,
          true,
          2,
          2,
        )}

        {renderNumberCell(
          styles,
          detalle?.acuM_MONTO_PAGO_MC,
          [styles.dataCell, styles.wTaTot],
          textStyle,
          false,
          2,
          2,
          moneda !== "UF",
        )}
      </View>
    );
  };

  const renderIncrementRow = (label, ama, pa, ta, options = {}) => {
    const { section = false, subtotal = false } = options;

    return (
      <View
        key={`inc-${label}-${ama}-${pa}-${ta}`}
        style={[
          styles.dataRow,
          section ? styles.incHeaderRow : undefined,
          subtotal ? styles.subtotalRow : undefined,
        ]}
        wrap={false}
      >
        <View style={[styles.dataCell, styles.wItem]}>
          <Text></Text>
        </View>

        <View style={[styles.dataCell, styles.wDesc]}>
          <Text
            style={[
              styles.descCell,
              section ? styles.incSectionLabel : styles.incDetailLabel,
              section
                ? styles.incSectionLabel
                : !subtotal
                  ? { paddingLeft: 8 }
                  : undefined,
              subtotal ? styles.incSectionLabel : undefined,
            ]}
          >
            {label}
          </Text>
        </View>

        <View style={[styles.dataCell, styles.wUnidad]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScCant]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScMeses]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScPU]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScTot]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wReaFr]}>
          <Text></Text>
        </View>

        <View style={[styles.dataCell, styles.wAmaCant]}>
          <Text></Text>
        </View>
        {renderNumberCell(
          styles,
          ama,
          [styles.dataCell, styles.wAmaTot],
          section || subtotal ? styles.mainRowText : undefined,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        <View style={[styles.dataCell, styles.wPaCant]}>
          <Text></Text>
        </View>
        {renderNumberCell(
          styles,
          pa,
          [styles.dataCell, styles.wPaTot],
          section || subtotal ? styles.mainRowText : undefined,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        <View style={[styles.dataCell, styles.wTaCant]}>
          <Text></Text>
        </View>
        {renderNumberCell(
          styles,
          ta,
          [styles.dataCell, styles.wTaTot],
          section || subtotal ? styles.mainRowText : undefined,
          false,
          2,
          2,
          moneda !== "UF",
        )}
      </View>
    );
  };

  const mainPartidas = rows;
  // rows ya viene desde construirPartidasConDetalle => son partidas principales (no subpartidas)

  const sumDetalle = (arr, key) =>
    arr.reduce((acc, r) => acc + toNumber(r?.detalle?.[key]), 0);

  const baseScTot = sumDetalle(mainPartidas, "montO_TOTCONT_MONTO_BASE_MC");
  const baseAmaTot = sumDetalle(mainPartidas, "acuM_ANTERIOR_MONTO_PAGO_MC");
  const basePaTot = sumDetalle(mainPartidas, "eP_MES_MONTO_PAGO_MC");
  const baseTaTot = sumDetalle(mainPartidas, "acuM_MONTO_PAGO_MC");

  // Totales finales = partidas + (incrementos - deducciones)
  const extraScTot = toNumber(hashTotals?.scTot);
  const extraAmaTot = toNumber(hashTotals?.amaTot);
  const extraPaTot = toNumber(hashTotals?.paTot);
  const extraTaTot = toNumber(hashTotals?.taTot);

  const grandScTot = baseScTot + extraScTot;
  const grandAmaTot = baseAmaTot + subAma + extraAmaTot;
  const grandPaTot = basePaTot + subPa;
  const grandTaTot = baseTaTot + subTa + extraTaTot;

  return (
    <View style={styles.table}>
      {/* Fila 1: encabezados de grupo */}
      <View style={styles.row} fixed>
        <View
          style={[styles.blankHeaderCell, styles.wItem, styles.rowSpanCell]}
        >
          <Text></Text>
        </View>

        <View
          style={[styles.blankHeaderCell, styles.wDesc, styles.rowSpanCell]}
        >
          <Text></Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wScGroup,
            styles.hGroup,
            styles.groupCell,
          ]}
        >
          <Text>Contrato</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wReaGroup,
            styles.hGroup,
            styles.groupCell,
          ]}
        >
          <Text>Reajuste</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wAmaGroup,
            styles.hGroup,
            styles.groupCell,
          ]}
        >
          <Text>Acumulado mes anterior</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wPaGroup,
            styles.hGroup,
            styles.groupCell,
          ]}
        >
          <Text>Período actual</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wTaGroup,
            styles.hGroup,
            styles.groupCell,
          ]}
        >
          <Text>Total acumulado</Text>
        </View>
      </View>

      {/* Fila 2: subencabezados */}
      <View style={[styles.row, { backgroundColor: "#f2f2f2" }]} fixed>
        <View style={[styles.cell, styles.wItem, styles.rowSpanCell]}>
          <Text>Item</Text>
        </View>

        <View
          style={[
            styles.cell,
            styles.wDesc,
            styles.rowSpanCell,
            {
              textAlign: "left",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
          ]}
        >
          <Text>Descripción del Item (ECO 01)</Text>
        </View>

        <View style={[styles.cell, styles.wUnidad, { fontWeight: "bold" }]}>
          <Text>Unidad</Text>
        </View>
        <View style={[styles.cell, styles.wScCant, { fontWeight: "bold" }]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wScMeses, { fontWeight: "bold" }]}>
          <Text>Meses</Text>
        </View>
        <View style={[styles.cell, styles.wScPU, { fontWeight: "bold" }]}>
          <Text>Unitario ($)</Text>
        </View>
        <View style={[styles.cell, styles.wScTot, { fontWeight: "bold" }]}>
          <Text>Total ($)</Text>
        </View>

        <View style={[styles.cell, styles.wReaFr, { fontWeight: "bold" }]}>
          <Text hyphenationCallback={(word) => [word]}>Factor reajuste</Text>
        </View>

        <View style={[styles.cell, styles.wAmaCant, { fontWeight: "bold" }]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wAmaTot, { fontWeight: "bold" }]}>
          <Text>Valor</Text>
        </View>

        <View style={[styles.cell, styles.wPaCant, { fontWeight: "bold" }]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wPaTot, { fontWeight: "bold" }]}>
          <Text>Valor</Text>
        </View>

        <View style={[styles.cell, styles.wTaCant, { fontWeight: "bold" }]}>
          <Text>Cantidad</Text>
        </View>
        <View style={[styles.cell, styles.wTaTot, { fontWeight: "bold" }]}>
          <Text>Valor</Text>
        </View>
      </View>

      {/* Filas de partidas */}
      {rows.map((partida) => (
        <View key={`group-${partida.partidA_ID}`}>
          {renderDataRow(partida, false)}
          {(partida?.sub_partidas ?? []).map((subPartida) =>
            renderDataRow(subPartida, true),
          )}
        </View>
      ))}

      {hashRows.map((row) => renderDataRow(row, false))}

      {/* Filas de incrementos/deducciones integradas */}
      <View wrap={false}>
        {renderIncrementRow("Incrementos", incAma, incPa, incTa, {
          section: true,
        })}
        {incRows.map((r) => renderIncrementRow(r.nombre, r.ama, r.pa, r.ta))}
      </View>
      <View wrap={false}>
        {renderIncrementRow("Deducciones", dedAma, dedPa, dedTa, {
          section: true,
        })}
        {dedRows.map((r) => renderIncrementRow(r.nombre, r.ama, r.pa, r.ta))}
        {renderIncrementRow(
          "SUBTOTAL INCREMENTOS Y DEDUCCIONES",
          subAma,
          subPa,
          subTa,
          { subtotal: true },
        )}
      </View>

      <View style={styles.dataRow} wrap={false}>
        <View style={[styles.dataCell, styles.wItem, styles.totalRowCell]}>
          <Text></Text>
        </View>

        <View style={[styles.dataCell, styles.wDesc, styles.totalRowCell]}>
          <Text style={[styles.descCell, styles.totalLabel]}>Totales</Text>
        </View>

        <View style={[styles.dataCell, styles.wUnidad, styles.totalRowCell]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScCant, styles.totalRowCell]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScMeses, styles.totalRowCell]}>
          <Text></Text>
        </View>
        <View style={[styles.dataCell, styles.wScPU, styles.totalRowCell]}>
          <Text></Text>
        </View>

        {renderNumberCell(
          styles,
          grandScTot,
          [styles.dataCell, styles.wScTot, styles.totalRowCell],
          styles.totalText,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        <View style={[styles.dataCell, styles.wReaFr, styles.totalRowCell]}>
          <Text></Text>
        </View>

        <View style={[styles.dataCell, styles.wAmaCant, styles.totalRowCell]}>
          <Text></Text>
        </View>
        {renderNumberCell(
          styles,
          grandAmaTot,
          [styles.dataCell, styles.wAmaTot, styles.totalRowCell],
          styles.totalText,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        <View style={[styles.dataCell, styles.wPaCant, styles.totalRowCell]}>
          <Text></Text>
        </View>
        {renderNumberCell(
          styles,
          grandPaTot,
          [styles.dataCell, styles.wPaTot, styles.totalRowCell],
          styles.totalText,
          false,
          2,
          2,
          moneda !== "UF",
        )}

        <View style={[styles.dataCell, styles.wTaCant, styles.totalRowCell]}>
          <Text></Text>
        </View>
        {renderNumberCell(
          styles,
          grandTaTot,
          [styles.dataCell, styles.wTaTot, styles.totalRowCell],
          styles.totalText,
          false,
          2,
          2,
          moneda !== "UF",
        )}
      </View>
    </View>
  );
}

export default ItemsTable;
